import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";

/* ---------------- UTIL ---------------- */
function calculateAgeFromDob(dob) {
  const birth = new Date(dob);
  const now = new Date();

  let years = now.getFullYear() - birth.getFullYear();
  let months = now.getMonth() - birth.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  }

  return { years, months };
}

/* ---------------- COMPONENT ---------------- */
function PatientDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  /* STATE */
  const [patient, setPatient] = useState(null);
  const [visits, setVisits] = useState([]);
  const [visitImages, setVisitImages] = useState({});

  const [symptoms, setSymptoms] = useState("");
  const [originalSymptoms, setOriginalSymptoms] = useState("");
  const [savingSymptoms, setSavingSymptoms] = useState(false);

  const [selectedImage, setSelectedImage] = useState(null);
  const [deletingImageId, setDeletingImageId] = useState(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmDeleteVisitId, setConfirmDeleteVisitId] = useState(null);


  /* ---------------- EFFECTS ---------------- */
  useEffect(() => {
    fetchPatient();
    fetchVisits();
  }, []);

  /* ---------------- API ---------------- */
  const fetchPatient = async () => {
    const res = await api.get(`/api/patients/${id}`);
    setPatient(res.data);
    setSymptoms(res.data.problem_text || "");
    setOriginalSymptoms(res.data.problem_text || "");
  };

  const fetchVisits = async () => {
    const res = await api.get(`/api/visits/${id}`);
    setVisits(res.data);
    res.data.forEach(v => fetchVisitImages(v.id));
  };

  const fetchVisitImages = async (visitId) => {
    try {
      const res = await api.get(`/api/visit-images/${visitId}`);
      setVisitImages(prev => ({ ...prev, [visitId]: res.data }));
    } catch (err) {
      console.error("Image fetch failed", err);
    }
  };

  const handleSaveSymptoms = async () => {
    try {
      setSavingSymptoms(true);
      await api.put(`/api/patients/${id}`, { problem_text: symptoms });
      setOriginalSymptoms(symptoms);
    } catch (err) {
      console.error("Save symptoms failed", err);
    } finally {
      setSavingSymptoms(false);
    }
  };

  const handleDeleteImage = async (imageId, visitId) => {
    try {
      setDeletingImageId(imageId);
      await api.delete(`/api/visit-images/${imageId}`);

      setVisitImages(prev => ({
        ...prev,
        [visitId]: prev[visitId].filter(img => img.id !== imageId),
      }));
    } catch (err) {
      console.error("Delete image failed", err);
    } finally {
      setDeletingImageId(null);
    }
  };

  const handleDeletePatient = async () => {
    try {
      await api.delete(`/api/patients/${id}`);
      navigate("/");
    } catch (err) {
      console.error("Delete patient failed", err);
    }
  };

  const handleDeleteVisit = async (visitId) => {
  try {
    await api.delete(`/api/visits/${visitId}`);
    setVisits(prev => prev.filter(v => v.id !== visitId));
    setConfirmDeleteVisitId(null);
  } catch (err) {
    console.error("Delete visit failed", err);
  }
};


  /* ---------------- RENDER GUARD ---------------- */
  if (!patient) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        Loading...
      </div>
    );
  }

  /* AGE */
  let ageText = "-";
  if (patient.dob) {
    const age = calculateAgeFromDob(patient.dob);
    ageText = `${age.years}y ${age.months}m`;
  } else if (patient.initial_age_years !== null) {
    ageText = `${patient.initial_age_years}y ${patient.initial_age_months || 0}m`;
  }

  const symptomsChanged = symptoms !== originalSymptoms;

  /* ---------------- JSX ---------------- */
  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      <div className="max-w-3xl mx-auto px-4 py-4">

        {/* BACK */}
        <button onClick={() => navigate("/")} className="text-blue-600 mb-4">
          ← Back
        </button>

        {/* PATIENT CARD */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="flex gap-3 items-center mb-2">
            <div className="min-w-[56px] h-12 bg-blue-600 text-white rounded-lg flex items-center justify-center font-bold text-lg">
              {patient.id}
            </div>
            <div>
              <h1 className="text-xl font-semibold">{patient.name}</h1>
              <p className="text-gray-600">📞 {patient.phone}</p>
              <p className="text-sm text-gray-700">🎂 {ageText}</p>
            </div>
          </div>

          {/* SYMPTOMS */}
          <div className="mt-4">
            <label className="text-sm font-medium block mb-1">
              Illness / Symptoms
            </label>
            <textarea
              rows={4}
              className="w-full border rounded-lg p-3 text-base focus:outline-none focus:ring"
              value={symptoms}
              onChange={(e) => setSymptoms(e.target.value)}
            />

            {symptomsChanged && (
              <button
                onClick={handleSaveSymptoms}
                disabled={savingSymptoms}
                className="mt-3 w-full h-11 bg-green-600 text-white rounded-lg font-medium"
              >
                {savingSymptoms ? "Saving..." : "Save Symptoms"}
              </button>
            )}
          </div>

          <button
            onClick={() => navigate(`/patients/${id}/visit/new`)}
            className="mt-4 w-full h-11 bg-blue-600 text-white rounded-lg font-medium"
          >
            Add Visit
          </button>
        </div>

        {/* VISITS */}
        <h2 className="text-lg font-semibold mb-3">Visits</h2>

        <div className="space-y-4">
          {visits.map((v) => (
  <div
    key={v.id}
    className="bg-white rounded-xl p-4 shadow-sm space-y-3"
  >
    {/* HEADER */}
    <div className="flex items-start justify-between">
      <div className="flex gap-3 items-center">
        {/* VISIT NUMBER */}
        <div className="min-w-[56px] h-12 bg-green-600 text-white rounded-lg flex items-center justify-center font-bold text-lg">
          #{v.visit_number}
        </div>

        {/* DATE / TIME */}
        <div className="text-sm text-gray-600 leading-tight">
          <div>📅 {v.visit_date}</div>
          <div>⏰ {v.visit_time}</div>
        </div>
      </div>

      {/* EDIT BUTTON */}
      <button
        onClick={() => navigate(`/visits/${v.id}/edit`)}
        className="text-sm text-blue-600"
      >
        Edit
      </button>
    </div>

    {/* NOTES */}
    {v.notes && (
      <div className="text-sm bg-gray-50 rounded-lg p-3">
        <span className="font-medium">Notes:</span> {v.notes}
      </div>
    )}

    {/* IMAGES */}
    {visitImages[v.id]?.length > 0 ? (
      <div className="grid grid-cols-3 gap-2">
        {visitImages[v.id].map((img) => (
          <div key={img.id} className="relative">
            <img
              src={`https://api.clinic.mrsinghdev.com${img.image_path}`}
              alt="Visit"
              onClick={() => setSelectedImage(img)}
              className="w-full h-24 object-cover rounded-lg border cursor-pointer"
            />

            {/* TYPE BADGE */}
            <div
              className={`absolute top-1 left-1 text-xs px-2 py-0.5 rounded text-white ${
                img.image_type === "test"
                  ? "bg-purple-600"
                  : "bg-blue-600"
              }`}
            >
              {img.image_type}
            </div>

            {/* DELETE IMAGE */}
            <button
              onClick={() => handleDeleteImage(img.id, v.id)}
              disabled={deletingImageId === img.id}
              className="absolute top-1 right-1 w-6 h-6 bg-black bg-opacity-60 text-white rounded-full text-xs flex items-center justify-center"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    ) : (
      <div className="text-xs text-gray-400">
        No images uploaded
      </div>
    )}

    {/* DANGER ZONE (OPTIONAL – IF YOU ADDED VISIT DELETE) */}
    <div className="pt-2 border-t">
      {confirmDeleteVisitId !== v.id ? (
        <button
          onClick={() => setConfirmDeleteVisitId(v.id)}
          className="text-xs text-red-600 underline"
        >
          Delete visit
        </button>
      ) : (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2">
          <p className="text-xs text-red-700 mb-2">
            This will permanently delete this visit.
          </p>

          <div className="flex gap-2">
            <button
              onClick={() => handleDeleteVisit(v.id)}
              className="px-2 py-1 bg-red-600 text-white rounded text-xs"
            >
              Yes, delete
            </button>
            <button
              onClick={() => setConfirmDeleteVisitId(null)}
              className="px-2 py-1 bg-gray-200 rounded text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
))}

        </div>

        {/* DANGER ZONE */}
        <div className="mt-10 border-t pt-4">
          

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
            >
              Delete patient
            </button>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700 mb-3">
                This will permanently delete the patient and all visits.
              </p>

              <div className="flex gap-2">
                <button
                  onClick={handleDeletePatient}
                  className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm"
                >
                  Yes, delete
                </button>

                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-2 bg-gray-200 rounded-lg text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FULL IMAGE MODAL */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black bg-opacity-60 text-white text-2xl flex items-center justify-center"
          >
            ×
          </button>

          <img
            src={`https://api.clinic.mrsinghdev.com${selectedImage.image_path}`}
            alt="Full view"
            className="max-h-full max-w-full rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default PatientDetails;
